/**
 * A rule-log entry's `changes`, in words: "Category: Dining → Groceries".
 *
 * The wire types each entry as an open object. The backend writes
 * `{field, action, previous_value, new_value}` per action (`rule_engine.py`
 * `apply_actions`); the values are ids, strings, string arrays or, for the two
 * exclude actions, JSON booleans. Nothing here trusts a shape: an entry that is
 * not an object, or carries no action, is skipped, and an action this client
 * does not know says only that its field changed, never a raw value.
 *
 * Ids resolve through `Names`. A category or merchant that no longer exists is
 * "Unknown", never its id; one still being looked up (a merchant, or every
 * category while the list loads) is "…".
 */
import type { Names } from './ruleText'

type Entry = { field: string; action: string; previous: unknown; next: unknown }

function entryOf(raw: unknown): Entry | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  if (typeof r.action !== 'string') return null
  return {
    field: typeof r.field === 'string' ? r.field : '',
    action: r.action,
    previous: r.previous_value,
    next: r.new_value,
  }
}

const NONE = 'None'
const UNKNOWN = 'Unknown'
const LOADING = '…'

const isEmpty = (v: unknown) => v === null || v === undefined || v === ''

function category(v: unknown, names: Names): string {
  if (isEmpty(v)) return NONE
  if (typeof v !== 'string') return UNKNOWN
  return names.category(v) ?? (names.categoriesLoading ? LOADING : UNKNOWN)
}

function merchant(v: unknown, names: Names): string {
  if (isEmpty(v)) return NONE
  if (typeof v !== 'string') return UNKNOWN
  const m = names.merchant(v)
  if (m.status === 'found') return m.name
  return m.status === 'loading' ? LOADING : UNKNOWN
}

const quoted = (v: unknown) => (isEmpty(v) ? NONE : typeof v === 'string' ? `“${v}”` : UNKNOWN)

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])

/** Tags in `a` that are not in `b`, each with one leading hash. */
const without = (a: unknown, b: unknown) => {
  const drop = new Set(strings(b))
  return strings(a)
    .filter((t) => !drop.has(t))
    .map((t) => `#${t.replace(/^#+/, '')}`)
}

/** What an append added: the new note's lines that were not there before. */
function appended(previous: unknown, next: unknown): string {
  if (typeof next !== 'string') return UNKNOWN
  const before = new Set(typeof previous === 'string' ? previous.split('\n') : [])
  const added = next.split('\n').filter((l) => !before.has(l))
  return quoted(added.join('\n'))
}

/** "something_new" → "Something new". */
const label = (field: string) => {
  const words = field.replace(/_/g, ' ').trim()
  return words === '' ? 'A field' : words.charAt(0).toUpperCase() + words.slice(1)
}

function line(e: Entry, names: Names): string {
  switch (e.action) {
    case 'set_category':
      return `Category: ${category(e.previous, names)} → ${category(e.next, names)}`
    case 'set_merchant':
      return `Merchant: ${merchant(e.previous, names)} → ${merchant(e.next, names)}`
    case 'add_tag':
      return `Tags: + ${without(e.next, e.previous).join(', ')}`
    case 'remove_tag':
      return `Tags: − ${without(e.previous, e.next).join(', ')}`
    case 'flag_for_review':
      return 'Flagged for review'
    case 'set_note':
      return `Note: ${quoted(e.previous)} → ${quoted(e.next)}`
    case 'append_note':
      return `Note: + ${appended(e.previous, e.next)}`
    case 'exclude_from_budget':
      return 'Budget: Included → Excluded'
    case 'exclude_from_reports':
      return 'Reports: Included → Excluded'
    default:
      return `${label(e.field)} changed`
  }
}

export function changeLines(changes: readonly unknown[], names: Names): string[] {
  return changes.flatMap((raw) => {
    const e = entryOf(raw)
    return e === null ? [] : [line(e, names)]
  })
}
